<template>
  <validation-observer ref="timeShiftForm" #default="{ invalid }">
    <n-form-confirmation
      key="timeShiftForm"
      :bypass="true"
      @submit="validateForm"
      :form="$refs.timeShiftForm"
      :disabled="invalid"
    >
      <b-card-title v-if="formTitle" class="mb-1">{{ formTitle }}</b-card-title>

      <b-alert class="p-1" variant="primary" show
        >{{ $t('label.yourTimezone') }}: {{ getLocaleTimezone(timezone, $i18n.locale) }}</b-alert
      >

      <!-- Multi-language Title Input -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('label.title')"
        :rules="
          locale === appLocale
            ? 'required|max:100'
            : 'max:100'
        "
        style="width: 100%;"
      >
        <b-form-group
          :label="$t('label.title')"
          :state="errors.length === 0 ? null : false"
          class="mb-3"
        >
          <div class="d-flex align-items-center">
            <div class="flex-grow-1 mr-1">
              <b-form-input
                v-model="formModel.title[locale]"
                :placeholder="$t('label.title')"
                :state="errors.length === 0 ? null : false"
              />
            </div>
            <div style="min-width: 80px">
              <b-form-select
                v-model="locale"
                :options="locales"
                class="language-select"
              />
            </div>
          </div>
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Other fields -->
      <n-input :fields="otherFields" v-model="formModel"></n-input>

      <b-row>
        <b-col cols="12" class="d-flex items-center justify-content-end">
          <b-button
            variant="secondary"
            class="close-btn mr-2"
            @click="closeModal"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <n-button-loading
            type="submit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :loading="loading"
            :disabled="invalid || loading"
          >
            {{ $t('button.save') }}
          </n-button-loading>
        </b-col>
      </b-row>
    </n-form-confirmation>
  </validation-observer>
</template>

<script>
  import { BRow, BCol, BButton, BAlert, BCardTitle, BFormGroup, BFormInput, BFormSelect } from 'bootstrap-vue';
  import FormInput from './timeShiftForm';
  import NInput from '@/components/NInput';
  import NFormConfirmation from '@/components/NFormConfirmation';
  import NButtonLoading from '@/components/NButtonLoading';
  import Repository from '@/repositories/RepositoryFactory';
  import Ripple from 'vue-ripple-directive';
  import cloneDeep from 'lodash/cloneDeep';
  import { getLocaleTimezone } from '@/utils/translate';

  const TimeShiftRepository = Repository.get('timeShift');

  export default {
    name: 'TimeShiftForm',
    components: {
      BAlert,
      BCardTitle,
      BFormGroup,
      BFormInput,
      BFormSelect,
      NInput,
      NFormConfirmation,
      BRow,
      BCol,
      BButton,
      NButtonLoading
    },
    props: {
      formTitle: {
        type: String
      },
      data: {
        type: Object,
        default: () => null
      }
    },
    directives: {
      Ripple
    },
    setup() {
      const allFields = FormInput;
      const otherFields = allFields.filter(field => field.key !== 'title');
      return {
        otherFields,
        getLocaleTimezone
      };
    },
    data() {
      return {
        formModel: {
          title: {
            en: '',
            zh: '',
            km: '',
            vi: '',
          }
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        loading: false,
        locale: this.$i18n.locale.split('-')[0],
        locales: [
          { value: 'en', text: 'English' },
          { value: 'zh', text: '中文' },
          { value: 'km', text: 'ខ្មែរ' },
          { value: 'vi', text: 'Tiếng Anh' },
        ],
      };
    },
    watch: {
      '$i18n.locale'(val) {
        const [language] = val.split('-')
        this.locale = language
      },
    },
    created() {
      this.initData();
    },
    computed: {
      appLocale() {
        return this.$i18n.locale.split('-')[0];
      }
    },
    methods: {
      initData() {
        console.log('>>> this.data', this.data);
        if (this.data) {
          const { timezone, title, titleTranslate } = this.data;

          this.timezone = timezone;
          this.formModel = cloneDeep(this.data);
          
          // Handle title conversion for multi-language support
          if (titleTranslate || title) {
            const sourceTitle = titleTranslate || title;
            this.formModel.title = this.convertTitleToObject(sourceTitle);
          }
        }
      },
      
      // Helper method to convert string title to object
      convertTitleToObject(title) {
        if (typeof title === 'string') {
          const defaultTitle = title
          const titleObj = {
            en: defaultTitle,
            zh: '',
            km: '',
            vi: '',
          }
          // Set the current locale's value
          const currentLocale = this.appLocale
          if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
            titleObj[currentLocale] = defaultTitle
          }
          return titleObj
        }
        return title
      },

      validateForm() {
        this.$refs.timeShiftForm.validate().then(success => {
          if (success) {
            this.submit();
          }
        });
      },

      async submit() {
        this.loading = true;

        const { endTime, title, ...rest } = this.formModel;
        const [hour, minute] = endTime.split(':');

        // Ensure title is an object before submission
        const formattedTitle = this.convertTitleToObject(title);

        const payload = {
          ...rest,
          titleTranslate: formattedTitle,
          endTime: hour + ':' + minute + ':59',
          timezone: this.timezone
        };

        try {
          const id = this.data?.id;
          const response = id
            ? await TimeShiftRepository.update(id, {
              ...payload,
              title: null, // remove title
            })
            : await TimeShiftRepository.create(payload);

          const timeShift = response.data.data;
          this.$emit('success', timeShift);
        } catch (error) {
          // Handle error if needed
        } finally {
          this.loading = false;
        }
      },

      closeModal() {
        this.$emit('hidden');
      }
    }
  };
</script>

<style lang="scss" scoped>
.language-select {
  height: 100%;
}
</style>
